import { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import {
  AttributeType,
  LevelType,
  PartialWithRequired,
  PositionAndDimensionsType,
  PositionType,
  SpaceType
} from 'data/types';
import {
  beginUndoBatch,
  endUndoBatch
} from 'features/structure/structure-history-middleware';
import {
  deleteStructureItem,
  updateStructureItem,
  updateStructureItemAttribute
} from 'features/structure/structure-slice';
import {
  applyLeftoverCarvePlans,
  applyLevelLeftoverRebuild,
  applyResizedSpaceAndLeftoverPlans,
  HYBRID_LEFTOVER_HOLE_MESSAGE,
  incomingSpacePatch,
  planLeftoverCarves,
  planLevelLeftoverRebuild
} from 'utils/apply-carve-leftover';
import { collectLevelUserPlacedSpaces } from 'utils/auto-layout-space';
import { leftoverRebuildTargetsForLevel } from 'utils/leftover-space-targets';
import { occupantSpacesForResize } from 'utils/rebuild-hybrid-leftover';
import { findLevelBySpaceId } from 'utils/structure';
import { warningModal } from 'utils/ui';

export { deleteUserSpacesAndRebuildLeftovers as deleteUserSpacesFromPalette } from 'features/structure/structure-thunks';
export {
  collisionSpacesForUserMove,
  gridSpacesExcludingLeftovers
} from 'utils/leftover-collision';
export {
  canPlaceIncomingSpaceOnRemainder,
  collisionSpacesForMove,
  leftoverRebuildTargetsForLevel,
  leftoverUniqueIdsForLevel
} from 'utils/leftover-space-targets';
export type { LeftoverRebuildTargets } from 'utils/leftover-space-targets';

export type UserSpaceGeometryCommit = {
  uniqueId: string;
  position: PositionType;
  width: number;
  length: number;
  vertices: PositionType[];
  squareFootage: number;
};

export type RemainderSyncState = {
  structure: LevelType[];
  autoLayoutSpaceIds: readonly string[];
  catalogAttributes: AttributeType[];
  footprint: PositionType[] | null;
};

export type IncomingSpaceRemainderResult = 'placed' | 'blocked' | 'skipped';

export type MovedSpaceGeometry = {
  position: PositionType;
  width?: number;
  length?: number;
  vertices: PositionType[];
};

export function rebuildRemainderAfterUserMove(
  dispatch: Dispatch<UnknownAction>,
  level: LevelType | null | undefined,
  draggedIds: ReadonlySet<string>,
  state: RemainderSyncState
): void {
  const targets = leftoverRebuildTargetsForLevel(
    level,
    state.autoLayoutSpaceIds,
    draggedIds
  );
  if (!targets) {
    return;
  }
  applyLevelLeftoverRebuild(
    dispatch,
    state.structure,
    targets.levelId,
    state.autoLayoutSpaceIds,
    state.catalogAttributes,
    state.footprint
  );
}

export function commitUserSpaceGeometryAndLeftovers(
  dispatch: Dispatch<UnknownAction>,
  structure: LevelType[],
  level: LevelType,
  autoLayoutSpaceIds: readonly string[],
  catalogAttributes: AttributeType[],
  geometry: UserSpaceGeometryCommit,
  fallbackFootprint?: PositionType[] | null
):
  | { ok: true; applied: boolean }
  | { ok: false; reason: 'hole' | 'no-footprint' } {
  if (
    !leftoverRebuildTargetsForLevel(level, autoLayoutSpaceIds, [
      geometry.uniqueId
    ])
  ) {
    return { ok: true, applied: false };
  }

  const occupants = occupantSpacesForResize(
    collectLevelUserPlacedSpaces(level, autoLayoutSpaceIds),
    geometry.uniqueId,
    {
      position: geometry.position,
      width: geometry.width,
      length: geometry.length,
      vertices: geometry.vertices
    }
  );
  const planned = planLevelLeftoverRebuild(
    level,
    autoLayoutSpaceIds,
    fallbackFootprint,
    occupants
  );
  if (!planned.ok) {
    return planned;
  }
  if (planned.plans.length === 0) {
    return { ok: true, applied: false };
  }

  applyResizedSpaceAndLeftoverPlans(
    dispatch,
    structure,
    level.uniqueId,
    geometry,
    planned.plans,
    catalogAttributes
  );
  return { ok: true, applied: true };
}

export function commitMovedSpaceGeometry(
  dispatch: Dispatch<UnknownAction>,
  spaceId: string,
  geometry: MovedSpaceGeometry,
  squareFootage: number,
  state: RemainderSyncState
): boolean {
  const level = findLevelBySpaceId(state.structure, spaceId);
  if (!level) {
    return true;
  }

  const leftoverResult = commitUserSpaceGeometryAndLeftovers(
    dispatch,
    state.structure,
    level,
    state.autoLayoutSpaceIds,
    state.catalogAttributes,
    {
      uniqueId: spaceId,
      position: geometry.position,
      width: geometry.width ?? 0,
      length: geometry.length ?? 0,
      vertices: geometry.vertices,
      squareFootage
    },
    state.footprint
  );
  if (!leftoverResult.ok) {
    if (leftoverResult.reason === 'hole') {
      warningModal(HYBRID_LEFTOVER_HOLE_MESSAGE);
    }
    return false;
  }
  if (leftoverResult.applied) {
    return true;
  }

  dispatch(
    updateStructureItem({
      uniqueId: spaceId,
      position: geometry.position,
      width: geometry.width,
      length: geometry.length,
      vertices: geometry.vertices
    } as PartialWithRequired<SpaceType, 'uniqueId'>)
  );
  dispatch(
    updateStructureItemAttribute({
      uniqueId: spaceId,
      attributeCode: 'square_footage',
      value: squareFootage
    })
  );
  return true;
}

export function tryPlaceIncomingSpaceOnRemainder(
  dispatch: Dispatch<UnknownAction>,
  targetLevelId: string,
  newSpaceUniqueId: string,
  incoming: PositionAndDimensionsType,
  state: RemainderSyncState,
  getLatestStructure: () => LevelType[]
): IncomingSpaceRemainderResult {
  const level = state.structure.find(
    (entry) => entry.uniqueId === targetLevelId
  );
  const targets = leftoverRebuildTargetsForLevel(
    level,
    state.autoLayoutSpaceIds,
    [newSpaceUniqueId]
  );
  const planned = targets
    ? planLeftoverCarves(targets.leftovers, incoming)
    : null;

  if (planned && !planned.ok && planned.reason === 'hole') {
    dispatch(deleteStructureItem({ uniqueId: newSpaceUniqueId }));
    warningModal(HYBRID_LEFTOVER_HOLE_MESSAGE);
    return 'blocked';
  }

  if (!planned?.ok) {
    return 'skipped';
  }

  dispatch(beginUndoBatch());
  dispatch(
    updateStructureItem({
      uniqueId: newSpaceUniqueId,
      ...incomingSpacePatch(incoming)
    } as SpaceType)
  );
  dispatch(
    updateStructureItemAttribute({
      uniqueId: newSpaceUniqueId,
      attributeCode: 'square_footage',
      value: Math.ceil(incoming.width * incoming.height)
    })
  );
  applyLeftoverCarvePlans(
    dispatch,
    getLatestStructure(),
    targetLevelId,
    planned.plans,
    state.catalogAttributes
  );
  dispatch(endUndoBatch());
  return 'placed';
}
